import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { GetPublicUploadUrlsManager } from '~/components/Upload/utils/GetPublicUploadUrlsManager';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { useAirStore } from '~/utils/ReduxUtils';

type PublicUploadUrlsManagerProviderProps = {
  children: ReactNode;
};

type PublicUploadUrlsManagerProviderContextValue = {
  manager: GetPublicUploadUrlsManager;
};

const defaultValue: PublicUploadUrlsManagerProviderContextValue = {
  manager: null as unknown as GetPublicUploadUrlsManager,
};

const PublicUploadUrlsManagerProviderContext = createContext(defaultValue);

export const PublicUploadUrlsManagerProvider = memo(({ children }: PublicUploadUrlsManagerProviderProps) => {
  const store = useAirStore();

  const value = useMemo<PublicUploadUrlsManagerProviderContextValue>(
    () => ({
      manager: new GetPublicUploadUrlsManager({ store }),
    }),
    [store],
  );

  return (
    <PublicUploadUrlsManagerProviderContext.Provider value={value}>
      {children}
    </PublicUploadUrlsManagerProviderContext.Provider>
  );
});

PublicUploadUrlsManagerProvider.displayName = 'PublicUploadUrlsManagerProvider';

export function usePublicUploadUrlsManager() {
  const context = useContext(PublicUploadUrlsManagerProviderContext);

  if (context === defaultValue) {
    const error = 'PublicUploadUrlsManagerProviderContext used outside of PublicUploadUrlsManagerProvider';
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: error,
      });
    }
  }

  return context;
}
